import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Loader2, Search, AlertTriangle, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  ["bookings", "Appointments"], ["users", "Members"], ["products", "Products"],
  ["services", "Services"], ["content", "Articles"], ["events", "Events"],
];

export default function CleanupPanel() {
  const qc = useQueryClient();
  const [type, setType] = useState("bookings");
  const [q, setQ] = useState("");
  const [onlyTest, setOnlyTest] = useState(true);
  const [sel, setSel] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["cleanup", type, q, onlyTest],
    queryFn: () => api.get(`/admin/cleanup/scan`, { params: { type, q, only_test: onlyTest } }).then((r) => r.data),
  });
  const records = data?.records || [];

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allIds = records.filter((r) => !r.protected).map((r) => r.id);
  const selectAll = () => setSel(sel.size === allIds.length ? new Set() : new Set(allIds));

  const del = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} ${type} record(s)? This cannot be undone. (Financial ledger is not affected.)`)) return;
    setDeleting(true);
    try {
      const { data: res } = await api.post("/admin/cleanup/delete", { type, ids });
      toast.success(`Deleted ${res.deleted} record(s)` + (res.skipped?.length ? ` · skipped ${res.skipped.length} protected` : ""));
      setSel(new Set());
      qc.invalidateQueries({ queryKey: ["cleanup"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="admin-cleanup-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><Trash2 size={22} className="text-[#B45151]" /> Cleanup</h2>
      <p className="text-sm text-[#4A5D6B] mb-1">Find and remove test/demo records. Likely-test rows are flagged. Admins and your own account can't be deleted, and financial records are never touched.</p>
      <div className="flex flex-wrap gap-2 my-4">
        {TYPES.map(([k, l]) => (
          <button key={k} onClick={() => { setType(k); setSel(new Set()); }} data-testid={`cleanup-type-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${type === k ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cleanup-search" placeholder="Search name, email, title…" className="eyw-input w-full !pl-9" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={onlyTest} onChange={(e) => setOnlyTest(e.target.checked)} data-testid="cleanup-only-test" /> Only likely-test</label>
        <button onClick={del} disabled={deleting || sel.size === 0} data-testid="cleanup-delete" className="eyw-btn !py-2 text-sm !bg-red-600 hover:!bg-red-700 disabled:opacity-50">
          {deleting ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={15} />} Delete selected ({sel.size})
        </button>
      </div>
      {isLoading ? <div className="p-8 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /></div>
        : records.length === 0 ? <div className="eyw-card p-8 text-center text-[#4A5D6B]">No matching records.</div>
        : (
          <div className="eyw-card overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F9F7F3] border-b border-[#E5E0D8] text-xs text-[#4A5D6B]">
              <input type="checkbox" checked={sel.size > 0 && sel.size === allIds.length} onChange={selectAll} data-testid="cleanup-select-all" />
              <span className="flex-1">{records.length} record(s)</span>
            </div>
            <div className="max-h-[520px] overflow-auto divide-y divide-[#F1EEE8]">
              {records.map((r) => (
                <label key={r.id} className={`flex items-center gap-3 px-4 py-2.5 text-sm ${r.protected ? "opacity-50" : "cursor-pointer hover:bg-[#F9F7F3]"}`} data-testid={`cleanup-row-${r.id}`}>
                  <input type="checkbox" disabled={r.protected} checked={sel.has(r.id)} onChange={() => toggle(r.id)} />
                  <span className="flex-1 min-w-0">
                    <span className="text-[#1A242B] truncate">{r.label}</span>
                    {r.sub && <span className="text-[#8FA89B]"> · {r.sub}</span>}
                  </span>
                  {r.looks_test && <span className="flex items-center gap-1 text-[11px] text-[#B5794E]"><AlertTriangle size={12} /> test?</span>}
                  {r.protected && <span className="flex items-center gap-1 text-[11px] text-[#5C8A72]"><ShieldCheck size={12} /> protected</span>}
                </label>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}
